import React, { useState, useEffect, useCallback, useRef } from 'react';
import { residentsAPI } from '../../../services/api';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  UserIcon,
  CheckIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const ResidentSearchModal = ({
  isOpen,
  onClose,
  onSelect,
  excludeHouseholdId = null,
  title = "Search Residents"
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedResident, setSelectedResident] = useState(null);
  const isSearchingRef = useRef(false); // Use ref instead of state to avoid dependency issues
  const [filters, setFilters] = useState({
    gender: '',
    min_age: '',
    max_age: '',
    purok_zone: ''
  });

  // Combined search logic with debouncing
  useEffect(() => {
    console.log('Search useEffect triggered with:', { isOpen, searchTerm, filters });

    if (!isOpen) {
      console.log('Modal not open, skipping search');
      // Reset search state when modal closes
      isSearchingRef.current = false;
      return;
    }

    // Only perform search after modal is open
    const timer = setTimeout(() => {
      console.log('Executing search after timeout with:', { searchTerm, filters });

      // Call search function directly to avoid dependency issues
      const performSearch = async () => {
        // Prevent concurrent searches using ref
        if (isSearchingRef.current) {
          console.log('Search already in progress, ignoring duplicate call');
          return;
        }

        try {
          isSearchingRef.current = true;
          setLoading(true);
          const params = {
            search: searchTerm,
            exclude_household_id: excludeHouseholdId,
            limit: 20,
            ...filters
          };

          // Remove empty filters
          Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null || params[key] === undefined) {
              delete params[key];
            }
          });

          console.log('Searching residents with params:', params);
          const response = await residentsAPI.getUnassignedResidents(params);
          console.log('API Response:', response.data);
          console.log('Residents data:', response.data.data);
          setResidents(response.data.data || []);
        } catch (error) {
          console.error('Error searching residents:', error);
          setResidents([]);
        } finally {
          setLoading(false);
          isSearchingRef.current = false;
        }
      };

      performSearch();
    }, searchTerm === '' ? 0 : 300); // No delay for initial load, 300ms delay for typing

    return () => {
      console.log('Cleaning up search timeout');
      clearTimeout(timer);
    };
  }, [searchTerm, filters, isOpen, excludeHouseholdId]); // Include all direct dependencies // Removed isSearching from dependencies

  const handleSelect = () => {
    if (selectedResident) {
      console.log('Selected resident data:', selectedResident);
      onSelect(selectedResident);
      onClose();
      setSelectedResident(null);
      setSearchTerm('');
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedResident(null);
    setSearchTerm('');
    isSearchingRef.current = false; // Reset search state using ref
    setFilters({
      gender: '',
      min_age: '',
      max_age: '',
      purok_zone: ''
    });
  };

  const getPhotoUrl = (resident) => {
    if (resident.profile_photo) {
      // If it's already a full URL, return as is
      if (resident.profile_photo.startsWith('http')) {
        return resident.profile_photo;
      }
      // Otherwise, construct the storage URL
      return `${import.meta.env.VITE_API_BASE_URL}/storage/${resident.profile_photo}`;
    }
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-6 border w-full max-w-4xl shadow-2xl rounded-lg bg-white min-h-[600px] max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg">
              <UserIcon className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, ID number, or contact..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <select
            value={filters.gender}
            onChange={(e) => setFilters(prev => ({ ...prev, gender: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Genders</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>

          <input
            type="number"
            placeholder="Min Age"
            value={filters.min_age}
            onChange={(e) => setFilters(prev => ({ ...prev, min_age: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          <input
            type="number"
            placeholder="Max Age"
            value={filters.max_age}
            onChange={(e) => setFilters(prev => ({ ...prev, max_age: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          <input
            type="text"
            placeholder="Purok/Zone"
            value={filters.purok_zone}
            onChange={(e) => setFilters(prev => ({ ...prev, purok_zone: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto max-h-[400px] border border-gray-200 rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : residents.length === 0 ? (
            <div className="text-center py-12">
              <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No residents found</h3>
              <p className="mt-2 text-sm text-gray-500">
                {searchTerm ? 'Try adjusting your search terms.' : 'No unassigned residents available.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {residents.map((resident) => (
                <div
                  key={resident.id}
                  onClick={() => setSelectedResident(resident)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 ${
                    selectedResident?.id === resident.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    {/* Photo */}
                    <div className="flex-shrink-0 h-12 w-12">
                      {getPhotoUrl(resident) ? (
                        <img
                          src={getPhotoUrl(resident)}
                          alt={resident.name}
                          className="h-12 w-12 rounded-full object-cover border-2 border-gray-200"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentNode.querySelector('.fallback-avatar').style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className={`fallback-avatar h-12 w-12 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm ${
                          getPhotoUrl(resident) ? 'hidden' : 'flex'
                        }`}
                      >
                        {resident.first_name.charAt(0)}{resident.last_name.charAt(0)}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {resident.name}
                        </p>
                        {selectedResident?.id === resident.id && (
                          <CheckIcon className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                      <div className="flex items-center space-x-4 mt-1">
                        <p className="text-xs text-gray-500">
                          ID: {resident.barangay_id_number}
                        </p>
                        {resident.age && (
                          <p className="text-xs text-gray-500">
                            Age: {resident.age}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 capitalize">
                          {resident.gender}
                        </p>
                      </div>
                      {resident.contact_number && (
                        <p className="text-xs text-gray-500 mt-1">
                          Contact: {resident.contact_number}
                        </p>
                      )}
                      {resident.address && (
                        <p className="text-xs text-gray-500 truncate">
                          Address: {resident.address}
                        </p>
                      )}
                      {resident.occupation && (
                        <p className="text-xs text-gray-500 truncate">
                          Occupation: {resident.occupation}
                        </p>
                      )}
                      {resident.birthplace && (
                        <p className="text-xs text-gray-500 truncate">
                          Birthplace: {resident.birthplace}
                        </p>
                      )}
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                          resident.household_status === 'unassigned'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {resident.household_status === 'unassigned' ? 'Available' : 'Assigned'}
                        </span>
                        {resident.is_household_head && (
                          <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                            Household Head
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            {residents.length > 0 && `${residents.length} resident${residents.length === 1 ? '' : 's'} found`}
          </p>
          <div className="flex space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedResident}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-2"
            >
              <SparklesIcon className="h-4 w-4" />
              <span>Add as Family Member</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResidentSearchModal;
